import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Pause, XCircle, AlertTriangle, Play } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import { apiClient } from "@/shared/services/apiClient";
import { API_ROUTES } from "@/shared/services/apiRoutes";
import { useSubscription } from "../contexts/SubscriptionContext";

interface ManageSubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function ManageSubscriptionModal({ open, onOpenChange, onSuccess }: ManageSubscriptionModalProps) {
  const { toast } = useToast();
  const { usage, refreshSubscription } = useSubscription();
  const [action, setAction] = useState<'pause' | 'cancel' | 'resume' | null>(null);
  const [pauseMonths, setPauseMonths] = useState<string>("1");
  const [loading, setLoading] = useState(false);

  const handlePause = async () => {
    setLoading(true);
    try {
      const response = await apiClient.post(API_ROUTES.BILLING_PAUSE, {
        pauseMonths: parseInt(pauseMonths)
      });

      if (!response.ok) {
        throw new Error(response.error || 'Failed to pause subscription');
      }

      toast({
        title: "Subscription Paused",
        description: `Your subscription has been paused for ${pauseMonths} month(s). It will automatically resume after this period.`,
      });

      onSuccess();
      onOpenChange(false);
      setAction(null);

      // Refresh subscription data without reloading page
      await refreshSubscription();
    } catch (error: any) {
      console.error('Error pausing subscription:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to pause subscription. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async () => {
    setLoading(true);
    try {
      const response = await apiClient.post(API_ROUTES.BILLING_RESUME);

      if (!response.ok) {
        throw new Error(response.error || 'Failed to resume subscription');
      }

      toast({
        title: "Subscription Resumed",
        description: "Your subscription has been reactivated successfully.",
      });

      onSuccess();
      onOpenChange(false);
      setAction(null);

      // Refresh subscription data without reloading page
      await refreshSubscription();
    } catch (error: any) {
      console.error('Error resuming subscription:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to resume subscription. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      const response = await apiClient.post(API_ROUTES.BILLING_CANCEL);

      if (!response.ok) {
        throw new Error(response.error || 'Failed to cancel subscription');
      }

      toast({
        title: "Subscription Canceled",
        description: "Your subscription has been canceled. You'll retain access until the end of your billing period.",
      });

      onSuccess();
      onOpenChange(false);
      setAction(null);

      // Refresh subscription data without reloading page
      await refreshSubscription();
    } catch (error: any) {
      console.error('Error canceling subscription:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to cancel subscription. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAction(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Subscription</DialogTitle>
        </DialogHeader>

        {!action ? (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Choose how you'd like to manage your subscription:
            </p>

            <div className="space-y-3">
              {usage?.is_paused ? (
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto p-4"
                  onClick={() => setAction('resume')}
                >
                  <Play className="mr-3 h-5 w-5 text-green-600" />
                  <div className="text-left">
                    <div className="font-medium">Resume Subscription</div>
                    <div className="text-xs text-muted-foreground">
                      Reactivate your paused subscription
                    </div>
                  </div>
                </Button>
              ) : !usage?.isTrialActive && usage?.subscription_status !== 'canceled' && !usage?.cancellationDate && (
                /* Only show pause option for active paid subscriptions (not trial, not canceled) */
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto p-4"
                  onClick={() => setAction('pause')}
                >
                  <Pause className="mr-3 h-5 w-5 text-yellow-600" />
                  <div className="text-left">
                    <div className="font-medium">Pause Subscription</div>
                    <div className="text-xs text-muted-foreground">
                      Temporarily pause for 1-6 months
                    </div>
                  </div>
                </Button>
              )}

              {/* Only show cancel button if subscription is not already canceled */}
              {usage?.subscription_status !== 'canceled' && !usage?.cancellationDate && (
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto p-4"
                  onClick={() => setAction('cancel')}
                >
                  <XCircle className="mr-3 h-5 w-5 text-red-600" />
                  <div className="text-left">
                    <div className="font-medium">Cancel Subscription</div>
                    <div className="text-xs text-muted-foreground">
                      Permanently cancel your subscription
                    </div>
                  </div>
                </Button>
              )}

              {/* Show message if subscription is already canceled */}
              {(usage?.subscription_status === 'canceled' || usage?.cancellationDate) && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">Subscription Canceled</p>
                  <p className="text-sm text-gray-700 mt-1">
                    Your subscription has been canceled. You will retain access until{' '}
                    {usage?.nextBillingDate ? new Date(usage.nextBillingDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : usage?.billingCycleEnd ? new Date(usage.billingCycleEnd).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'the end of your billing period'}.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : action === 'resume' ? (
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-green-900">Resume Your Subscription</p>
                <p className="text-green-700 mt-1">
                  Your subscription will be reactivated immediately and you'll regain access to all premium features.
                </p>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setAction(null)}
                disabled={loading}
              >
                Back
              </Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={handleResume}
                disabled={loading}
              >
                {loading ? 'Resuming...' : 'Confirm Resume'}
              </Button>
            </div>
          </div>
        ) : action === 'pause' ? (
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-yellow-900">Pause Your Subscription</p>
                <p className="text-yellow-700 mt-1">
                  Your subscription will be paused and automatically resume after the selected period.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pause-duration">Pause Duration</Label>
              <Select value={pauseMonths} onValueChange={setPauseMonths}>
                <SelectTrigger id="pause-duration">
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent className="z-[60]">
                  <SelectItem value="1">1 Month</SelectItem>
                  <SelectItem value="2">2 Months</SelectItem>
                  <SelectItem value="3">3 Months</SelectItem>
                  <SelectItem value="4">4 Months</SelectItem>
                  <SelectItem value="5">5 Months</SelectItem>
                  <SelectItem value="6">6 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setAction(null)}
                disabled={loading}
              >
                Back
              </Button>
              <Button
                className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                onClick={handlePause}
                disabled={loading}
              >
                {loading ? 'Pausing...' : 'Confirm Pause'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-red-900">Cancel Your Subscription</p>
                <p className="text-red-700 mt-1">
                  If you cancel, you will permanently lose access to your account, including all saved videos, contacts, call history, and stored content. This action cannot be undone. You may resubscribe later, but your account will be brand new and none of your previous data can be restored.
                </p>
              </div>
            </div>

            {(usage?.nextBillingDate || usage?.billingCycleEnd) && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm">
                  <p className="font-medium text-blue-900">Access Until</p>
                  <p className="text-blue-700 mt-1">
                    You will remain active until{' '}
                    <span className="font-semibold">
                      {new Date(usage?.nextBillingDate || usage?.billingCycleEnd || '').toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setAction(null)}
                disabled={loading}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleCancel}
                disabled={loading}
              >
                {loading ? 'Canceling...' : 'Confirm Cancellation'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
